function buildSignatureRequestQuery(params: SignatureRequestPaginationRequest): Record<string, string | undefined> {
  return {
    page: params.page?.toString(),
    size: params.size?.toString(),
    sort: params.sortOrder,
    date_from: params.dateFrom,
    date_to: params.dateTo,
    date_time_mode: params.dateTimeMode,
    sort_by: params.sortBy,
    status: params.status?.join(','),
    search: params.search,
    send_by: params.sendBy,
  }
}

const srRepository = (fetch: CustomFetch) => ({
  async get(id: string) {
    return fetch<SignatureRequestData>(`/v3/signature-requests/${id}`)
  },
  /**
   * Get the paginated signature requests
   * Parameters:
   * - `page` - which page to return (triggers pagination response)
   * - `size` - the number of items per page
   * - `sort_by` - should be `name` | `date` | `status` - if omitted uses default by status
   * - `sort_order` - should be `asc` or `desc` (column: date)
   * - `date_from` and `date_to` - should get passed as YYYY-MM-DD format
   * - `date_time_mode` - should be `current_time` or `full_day` - if omitted uses default full_day
   * - `search` - search is separated from filters - when it is used, ignore filtering and sorting
   * - `status` - status of the signature request - if omitted returns all besides drafts
   * - `send_by` - user_id of the current_user to identify what has been send by the user
   */
  async getItems(request: SignatureRequestPaginationRequest) {
    const query = buildSignatureRequestQuery(request)
    // TODO: Set correct endpoint url before go-live
    return fetch<SignatureRequestPaginationResponse>(`/v3/signature-requests-redesign`, { query })
  },
  async getStatus(id: string) {
    return fetch<SignatureRequestStatus>(`/v3/signature-requests/${id}/status`)
  },
  async create(documents: SignatureRequestDocument[], title: string) {
    return fetch<SignatureRequestData>('/v3/signature-requests', {
      method: 'POST',
      body: {
        title: title,
        documents: documents,
        message: '',
        signatures: [],
      },
    })
  },
  async initiate(id: string) {
    // move SR from DRAFT to OPEN state
    return fetch<SignatureRequestData>(`/v3/signature-requests/${id}/initiate`, {
      method: 'POST',
      body: {},
    })
  },
  async update(
    id: string,
    updateData:
      | Partial<SignatureRequestData>
      | (Omit<SignatureRequestData, 'signatures'> & { signatures: Partial<SignatureData>[] })
      | (Omit<SignatureRequestData, 'observers'> & { observers: string[] })
  ) {
    return fetch<SignatureRequestData>(`/v3/signature-requests/${id}`, {
      method: 'PUT',
      body: objectToSnakeCase(updateData),
    })
  },
  async getEvents(id: string) {
    return fetch<SignatureRequestEvent[]>(`/v3/signature-requests/${id}/events`)
  },
  async getDocumentsZip(signatureRequestId: string, isSigned?: boolean) {
    const srOptions: Record<string, string> = {}
    if (isSigned) srOptions.signed = 'true'

    const response = await fetch.raw<Blob>(`/v3/signature-requests/${signatureRequestId}/zip`, {
      query: srOptions,
    })

    const data = response._data!
    const name = extractFilename(response)

    return {
      data,
      name: name ?? `${signatureRequestId}.zip`,
    }
  },
  async addDocument(id: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}/documents`, {
      method: 'POST',
    })
  },
  async removeDocument(id: string, docId: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}/documents/${docId}`, {
      method: 'DELETE',
    })
  },
  async checkLock(signatureRequestId: string) {
    return fetch<{ locked: boolean }>(`/v3/signature-requests/${signatureRequestId}/lock-status`)
  },
  async decline(id: string, message?: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}/decline`, {
      method: 'POST',
      body: { message },
    })
  },
  async withdraw(id: string, message?: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}/withdraw`, {
      method: 'POST',
      body: { message },
    })
  },
  async remove(id: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}`, {
      method: 'DELETE',
    })
  },
  async remindSigners(id: string): Promise<void> {
    try {
      await fetch(`/v3/signature-requests/${id}/remind`, {
        method: 'POST',
      })
    } catch {
      throw new Error('Failed to remind signers')
    }
  },
  async delegate(srId: string, delegateData: SignatureDelegateData) {
    return await fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${srId}/delegate`, {
      method: 'POST',
      body: delegateData,
    })
  },
  async inviteSigner(srId: string, email: string, documentIds: string[]) {
    try {
      await fetch(`/v3/signature-requests/${srId}/signatures`, {
        method: 'POST',
        body: {
          account_email: email,
          signer_identity_data: {
            email_address: email,
          },
          document_signatures: documentIds.map(id => ({
            document_id: id,
          })),
        },
      })
    } catch {
      throw new Error('Failed to invite signer')
    }
  },
  async removeSigner(id: string, signatureId: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}/signatures/${signatureId}`, {
      method: 'DELETE',
    })
  },
  async addObserver(srId: string, observerData: CreateObserverData) {
    try {
      return await fetch<ObserverData>(`/v3/signature-requests/${srId}/observers`, {
        method: 'POST',
        body: objectToSnakeCase(observerData),
      })
    } catch {
      throw new Error('Failed to add observer')
    }
  },
  async removeObserver(id: string, observerId: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}/observers/${observerId}`, {
      method: 'DELETE',
    })
  },
  async removeExpiration(id: string) {
    return fetch<{ status: DocumentActionResult }>(`/v3/signature-requests/${id}/expires-at`, {
      method: 'DELETE',
    })
  },
  async getUnclaimed(id: string) {
    return fetch<SendToResponse>(`/v3/sendto/${id}/get-internal`)
  },
  async claim(id: string) {
    try {
      return fetch<SignatureRequestData>(`/v3/sendto/${id}/claim`, {
        method: 'POST',
      })
    } catch {
      throw new Error('Failed to claim signature request')
    }
  },
})

export default srRepository
